// RotaSchedulerPage.jsx — Manager / admin rota scheduler.
//
// Can be rendered standalone (route `/rota`) or embedded inside an
// attendance page (pass embedded={true} to suppress the MessengerLayout).
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { AuthContext } from "../../context/AuthContext";
import ConflictPanel from "../../components/rota/ConflictPanel";
import ShiftEditPopover from "../../components/rota/ShiftEditPopover";
import {
  ChevronLeft, ChevronRight, Wand2, Save, Send, Lock,
  Megaphone, AlertTriangle, AlertCircle, Plus,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON  = "#00FF99";
const AMBER = "#EF9F27";
const BLUE  = "#378ADD";
const PURP  = "#7F77DD";
const RED   = "#E24B4A";

const TYPE_COLOR = {
  morning:   BLUE,
  afternoon: AMBER,
  night:     PURP,
  oncall:    "#888",
  off:       "#333",
  custom:    NEON,
};

const authHeaders = () => ({
  Authorization: `Bearer ${
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    ""
  }`,
});

const isoToday = () => new Date().toISOString().slice(0, 10);

const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - day);
  return x.toISOString().slice(0, 10);
};

const addDaysIso = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const fmtDay = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit" });
};

export default function RotaSchedulerPage({ embedded = false, fixedDepartment }) {
  const { user } = useContext(AuthContext);
  const [department, setDepartment]   = useState(fixedDepartment || "Engineering");
  const [weekStart,  setWeekStart]    = useState(startOfWeek(new Date()));
  const [rotas,      setRotas]        = useState([]);
  const [activeId,   setActiveId]     = useState(null);
  const [detail,     setDetail]       = useState(null);
  const [templates,  setTemplates]    = useState([]);
  const [loading,    setLoading]      = useState(false);
  const [error,      setError]        = useState(null);
  const [savingMsg,  setSavingMsg]    = useState("");
  const [popover,    setPopover]      = useState(null); // {shift, anchor, userName}

  const isAdmin   = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isAdmin || user?.role === "executive";

  // ── Loaders
  const loadList = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (weekStart)  qs.set("week_start", weekStart);
      if (department) qs.set("department", department);
      const r = await fetch(`${API_BASE}/api/rota/list?${qs}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Could not load rotas (HTTP ${r.status})`);
      const d = await r.json();
      setRotas(d);
      if (d.length && !activeId) setActiveId(d[0].id);
    } catch (e) {
      console.error("Rota list error:", e);
      setError(e.message || "Failed to load rotas");
    }
  }, [weekStart, department, activeId]);

  const loadDetail = useCallback(async () => {
    if (!activeId) { setDetail(null); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/rota/${activeId}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Could not load rota detail (HTTP ${r.status})`);
      setDetail(await r.json());
    } catch (e) {
      console.error("Rota detail error:", e);
      setError(e.message || "Failed to load rota detail");
    }
    setLoading(false);
  }, [activeId]);

  const loadTemplates = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/rota/templates`, { headers: authHeaders() });
    if (r.ok) setTemplates(await r.json());
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  // ── Actions
  const createRota = async () => {
    setError(null);
    try {
      const weekEnd = addDaysIso(weekStart, 6);
      const r = await fetch(`${API_BASE}/api/rota/create`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${department} W/C ${weekStart}`,
          department, week_start: weekStart, week_end: weekEnd,
        }),
      });
      if (!r.ok) throw new Error(`Create failed (HTTP ${r.status}): ${await r.text()}`);
      const d = await r.json();
      setActiveId(d.rota.id);
      await loadList();
    } catch (e) {
      console.error("Create rota error:", e);
      setError(e.message || "Failed to create rota");
    }
  };

  const autoSchedule = async () => {
    if (!activeId) return;
    setSavingMsg("Generating…"); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/rota/${activeId}/auto-schedule`, {
        method: "POST", headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`Auto-schedule failed (HTTP ${r.status})`);
      const d = await r.json();
      // Save the proposed shifts via PUT
      const upd = await fetch(`${API_BASE}/api/rota/${activeId}/shifts`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(d.shifts),
      });
      if (!upd.ok) throw new Error(`Could not save proposed shifts (HTTP ${upd.status})`);
      setDetail(await upd.json());
      setSavingMsg("Auto-scheduled at " + new Date().toLocaleTimeString());
    } catch (e) {
      console.error("Auto-schedule error:", e);
      setSavingMsg("");
      setError(e.message || "Auto-schedule failed");
    }
  };

  const saveShift = async (item) => {
    setSavingMsg("Saving…"); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/rota/${activeId}/shifts`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify([item]),
      });
      if (!r.ok) throw new Error(`Save failed (HTTP ${r.status}): ${await r.text()}`);
      setDetail(await r.json());
      setPopover(null);
      setSavingMsg("Saved at " + new Date().toLocaleTimeString());
    } catch (e) {
      console.error("Save shift error:", e);
      setSavingMsg("");
      setError(e.message || "Failed to save shift");
    }
  };

  const publish = async () => {
    setSavingMsg("Publishing…"); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/rota/${activeId}/status`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ status: "published" }),
      });
      if (r.status === 409) {
        const data = await r.json();
        const msg = (data.detail?.message || data.detail) + " — resolve errors before publishing.";
        setError(msg);
        setSavingMsg(""); return;
      }
      if (!r.ok) throw new Error(`Publish failed (HTTP ${r.status})`);
      setSavingMsg("Published — staff notified");
      await loadDetail(); await loadList();
    } catch (e) {
      console.error("Publish error:", e);
      setSavingMsg("");
      setError(e.message || "Publish failed");
    }
  };

  const leaveAction = async (leaveId, kind) => {
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/leave/${leaveId}/${kind}`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ comment: `Via rota scheduler — ${kind}` }),
      });
      if (!r.ok) throw new Error(`Leave ${kind} failed (HTTP ${r.status})`);
      await loadDetail();
    } catch (e) {
      console.error("Leave action error:", e);
      setError(e.message || `Leave ${kind} failed`);
    }
  };

  // ── Derived
  const days = useMemo(() => {
    if (!detail) return [];
    const out = [];
    for (let i = 0; i < 7; i++) out.push(addDaysIso(detail.rota.week_start, i));
    return out;
  }, [detail]);

  // ── Render
  const inner = (
    <div style={{ maxWidth: 1300, margin: "0 auto" }}>
      {/* Persistent error banner — surfaces any silent failure */}
      {error && (
        <div style={{
          background: "rgba(226,75,74,0.1)",
          border: "0.5px solid rgba(226,75,74,0.3)",
          borderRadius: 8,
          padding: "10px 14px",
          color: RED,
          fontSize: 13,
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}>
          <span>⚠ {error}</span>
          <button onClick={() => setError(null)} style={{
            marginLeft: "auto", background: "transparent", border: "none",
            color: RED, cursor: "pointer", fontSize: 13,
          }}>×</button>
        </div>
      )}

      {/* TOP BAR */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap",
      }}>
        {!embedded && (
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#fff" }}>Rota Scheduler</h1>
        )}
        {!fixedDepartment ? (
          <select value={department} onChange={(e) => setDepartment(e.target.value)} style={inpSel}>
            {["Engineering", "Product", "QA", "HR", "Ops", "Finance", "Sales"].map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        ) : (
          <span style={pill}>{department}</span>
        )}
        {detail && (
          <span style={{ ...pill, color: detail.rota.status === "published" ? NEON : AMBER,
                         borderColor: detail.rota.status === "published" ? `${NEON}55` : `${AMBER}55` }}>
            {detail.rota.status}
          </span>
        )}

        {/* Week nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
          <button onClick={() => setWeekStart(addDaysIso(weekStart, -7))} style={iconBtn}><ChevronLeft size={14} /></button>
          <input type="date" value={weekStart} onChange={(e) => setWeekStart(startOfWeek(e.target.value))}
                 style={{ ...inpSel, padding: "5px 8px" }} />
          <button onClick={() => setWeekStart(addDaysIso(weekStart, 7))} style={iconBtn}><ChevronRight size={14} /></button>
        </div>
      </div>

      {/* Rota list / create */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {rotas.length === 0 && (
          <p style={{ fontSize: 12, color: "#666", margin: 0 }}>No rotas for this week + dept yet.</p>
        )}
        {rotas.map((r) => (
          <button
            key={r.id}
            onClick={() => setActiveId(r.id)}
            style={{
              ...pill,
              cursor: "pointer",
              background: activeId === r.id ? "rgba(0,255,153,0.08)" : "transparent",
              borderColor: activeId === r.id ? `${NEON}77` : "#2a2a2a",
              color: activeId === r.id ? NEON : "#bbb",
            }}
          >
            {r.name}
            {r.conflict_count > 0 && (
              <span style={{ marginLeft: 6, color: RED }}>● {r.conflict_count}</span>
            )}
          </button>
        ))}
        {isManager && (
          <button onClick={createRota} style={{ ...pill, color: NEON, borderColor: `${NEON}55`, cursor: "pointer" }}>
            <Plus size={11} style={{ verticalAlign: "middle" }} /> New rota
          </button>
        )}
      </div>

      {/* Action bar */}
      {detail && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          <button onClick={autoSchedule} style={btnOutline}>
            <Wand2 size={12} /> Auto-schedule
          </button>
          <button onClick={loadDetail} style={btnOutline}>
            <Save size={12} /> Refresh
          </button>
          <div style={{ flex: 1 }} />
          <button
            onClick={publish}
            disabled={!detail.can_publish}
            style={detail.can_publish ? btnPrimary : btnLocked}
            title={detail.can_publish ? "Publish to all staff" : `Resolve ${detail.error_count} errors first`}
          >
            {detail.can_publish ? <Send size={12} /> : <Lock size={12} />}
            Publish rota
            {!detail.can_publish && detail.error_count > 0 && (
              <span style={{ marginLeft: 4 }}>· {detail.error_count}</span>
            )}
          </button>
        </div>
      )}

      {/* Conflict banner */}
      {detail && detail.conflicts.length > 0 && (
        <div style={{
          padding: "8px 12px", borderRadius: 10, marginBottom: 12,
          background: detail.error_count > 0 ? "rgba(226,75,74,0.08)" : "rgba(239,159,39,0.06)",
          border: `0.5px solid ${detail.error_count > 0 ? RED : AMBER}55`,
          color: detail.error_count > 0 ? RED : AMBER,
          fontSize: 12, display: "flex", alignItems: "center", gap: 8,
        }}>
          {detail.error_count > 0 ? <AlertCircle size={14} /> : <AlertTriangle size={14} />}
          {detail.error_count} error{detail.error_count === 1 ? "" : "s"}, {detail.warning_count} warning{detail.warning_count === 1 ? "" : "s"} — resolve before publishing
        </div>
      )}

      {/* Grid */}
      {loading && <p style={{ color: "#666" }}>Loading…</p>}
      {detail && (
        <div style={{
          background: "#0d0d0d",
          border: "0.5px solid #1c1c1c",
          borderRadius: 12,
          overflowX: "auto",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Team</th>
                {days.map((d) => (
                  <th key={d} style={{ ...th,
                    color: d === isoToday() ? NEON : "#888",
                  }}>{fmtDay(d)}{d === isoToday() && "*"}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.members.map((m) => (
                <tr key={m.user_id}>
                  <td style={tdName}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={avatar}>{m.user_initials}</span>
                      <div>
                        <div style={{ fontSize: 12, color: "#ddd" }}>{m.user_name}</div>
                        <div style={{ fontSize: 10, color: "#555" }}>{m.weekly_hours.toFixed(1)}h</div>
                      </div>
                    </div>
                  </td>
                  {days.map((d) => {
                    const sh = m.shifts.find((x) => x.shift_date === d);
                    if (!sh) {
                      return <td key={d} style={tdCell}>—</td>;
                    }
                    const isLeave = sh.leave_status === "approved";
                    const isPending = sh.leave_status === "pending";
                    const bg = isLeave ? RED : isPending ? AMBER : (TYPE_COLOR[sh.shift_type] || "#333");
                    return (
                      <td key={d} style={tdCell}>
                        <button
                          onClick={(e) => !isLeave && setPopover({ shift: sh, anchor: e.currentTarget, userName: m.user_name })}
                          disabled={isLeave}
                          style={{
                            display: "block", width: "100%", padding: "5px 6px", borderRadius: 6,
                            background: isLeave ? "rgba(226,75,74,0.12)" :
                                        isPending ? "rgba(239,159,39,0.12)" :
                                        `${bg}22`,
                            border: isPending ? `1px dashed ${AMBER}` : `0.5px solid ${bg}66`,
                            color: bg, fontSize: 10, fontWeight: 500, cursor: isLeave ? "not-allowed" : "pointer",
                            position: "relative",
                          }}
                        >
                          {isLeave ? "On leave" :
                           isPending ? "Leave?" :
                           sh.shift_type === "off" ? "Off" :
                           sh.shift_type === "oncall" ? "On-call" :
                           sh.shift_type[0].toUpperCase() + sh.shift_type.slice(1)}
                          {(isLeave || isPending) && (
                            <span style={{
                              position: "absolute", top: -3, right: -3,
                              width: 6, height: 6, borderRadius: "50%",
                              background: isLeave ? RED : AMBER,
                            }} />
                          )}
                          {!isLeave && !isPending && sh.shift_type !== "off" && (
                            <div style={{ fontSize: 9, color: `${bg}aa`, marginTop: 1 }}>
                              {sh.start_time}-{sh.end_time}
                            </div>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Coverage bar */}
          <div style={{ borderTop: "0.5px solid #1c1c1c", padding: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: `200px repeat(${days.length}, 1fr)`, gap: 4 }}>
              <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Coverage
              </div>
              {detail.coverage.map((c, i) => (
                <div key={c.date} style={{
                  fontSize: 10, padding: 6, borderRadius: 6,
                  background: c.total_on >= 3 ? "rgba(0,255,153,0.06)" :
                              c.total_on >= 1 ? "rgba(239,159,39,0.06)" :
                                                "rgba(226,75,74,0.06)",
                  color: c.total_on >= 3 ? NEON : c.total_on >= 1 ? AMBER : RED,
                  textAlign: "center",
                }}>
                  {c.total_on} on
                  <div style={{ fontSize: 8, color: "#555" }}>
                    M:{c.morning} A:{c.afternoon} N:{c.night}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Conflicts */}
      {detail && (
        <ConflictPanel conflicts={detail.conflicts} onLeaveAction={leaveAction} />
      )}

      {/* Bottom bar */}
      {detail && (
        <div style={{
          marginTop: 12, padding: "8px 12px",
          background: "#0a0a0a", border: "0.5px solid #1c1c1c", borderRadius: 10,
          fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 12,
        }}>
          <span>{savingMsg || `Last updated: ${detail.rota.created_at?.slice(0, 16).replace("T", " ")}`}</span>
          <div style={{ flex: 1 }} />
          {detail.rota.status === "published" && (
            <button style={{ ...btnOutline, padding: "4px 10px" }}>
              <Megaphone size={11} /> Notify team
            </button>
          )}
        </div>
      )}

      {/* Popover */}
      {popover && (
        <ShiftEditPopover
          shift={popover.shift}
          anchor={popover.anchor}
          templates={templates}
          userName={popover.userName}
          onClose={() => setPopover(null)}
          onSave={saveShift}
          onLeaveAction={leaveAction}
        />
      )}
    </div>
  );

  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

const pill = {
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "3px 10px", borderRadius: 999, fontSize: 11,
  border: "0.5px solid #2a2a2a", background: "transparent", color: "#bbb",
};

const inpSel = {
  background: "#0a0a0a", border: "0.5px solid #2a2a2a",
  borderRadius: 8, padding: "6px 10px", fontSize: 12,
  color: "#fff", outline: "none",
};

const iconBtn = {
  background: "transparent", border: "0.5px solid #2a2a2a",
  borderRadius: 8, padding: 4, color: "#888", cursor: "pointer",
};

const btnOutline = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", fontSize: 11, borderRadius: 8,
  border: `0.5px solid ${NEON}55`, background: "transparent",
  color: NEON, cursor: "pointer",
};

const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 14px", fontSize: 11, borderRadius: 8,
  border: `0.5px solid ${NEON}77`, background: `${NEON}1f`,
  color: NEON, cursor: "pointer", fontWeight: 500,
};

const btnLocked = {
  ...btnPrimary,
  borderColor: "#333",
  background: "rgba(255,255,255,0.04)",
  color: "#666",
  cursor: "not-allowed",
};

const th = {
  padding: "8px 6px", fontSize: 10, color: "#888",
  textTransform: "uppercase", letterSpacing: 0.5,
  borderBottom: "0.5px solid #1c1c1c", textAlign: "left",
};

const tdName = { padding: "8px 10px", borderBottom: "0.5px solid #131313", minWidth: 180 };
const tdCell = { padding: "5px 5px", borderBottom: "0.5px solid #131313", verticalAlign: "middle" };

const avatar = {
  width: 28, height: 28, borderRadius: "50%",
  background: "rgba(55,138,221,0.12)", color: BLUE,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 600, flexShrink: 0,
};
